var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
if (process.env.NODE_ENV !== "production") {
    import("@babylonjs/core/Debug/debugLayer");
    import("@babylonjs/inspector");
}
// ES6 IMPORTS
// if there are cases where es6 dependencies could be causing issues just try and load the whole babylon core, and
// that fixes it, then it is a dependencies issue, check this link out for answers.
// bjs post: https://forum.babylonjs.com/t/pickedmesh-is-null-in-onpointerobservable-after-update-to-6-25-0/45076/7
// bjs docs: https://doc.babylonjs.com/setup/frameworkPackages/es6Support#faq
import "@babylonjs/core/Culling/ray";
import "@babylonjs/core/Animations/animatable";
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";
import "@babylonjs/core/Loading/loadingScreen";
import "@babylonjs/loaders/glTF/2.0/glTFLoader";
import "@babylonjs/loaders/glTF/2.0/Extensions/KHR_materials_pbrSpecularGlossiness";
import "@babylonjs/loaders/glTF/2.0/Extensions/KHR_draco_mesh_compression";
import "@babylonjs/core/Rendering/depthRendererSceneComponent";
import "@babylonjs/core/Rendering/outlineRenderer";
import "@babylonjs/core/Audio/audioSceneComponent";
import { DracoCompression } from "@babylonjs/core/Meshes/Compression/dracoCompression";
DracoCompression.Configuration = {
    decoder: {
        wasmUrl: "/lib/draco_wasm_wrapper_gltf.js",
        wasmBinaryUrl: "/lib/draco_decoder_gltf.wasm",
        fallbackUrl: "/lib/draco_decoder_gltf.js",
    },
};
import { Engine } from "@babylonjs/core/Engines/engine";
import State from "./Screens/Screens";
import { LoginScene } from "./Screens/LoginScene";
import { CharacterSelectionScene } from "./Screens/CharacterSelection";
import { CharacterEditor } from "./Screens/CharacterEditor";
import { GameScene } from "./Screens/GameScene";
import { DebugScene } from "./Screens/DebugScene";
import { Config } from "../../shared/Config";
import { Loading } from "./Controllers/Loading";
import { isLocal } from "./Utils";
import { engineOptionsFromQuery, describeEngineOptions } from "./Utils/engineOptions";
import { GameController } from "./Controllers/GameController";
// App class is our entire game application
class App {
    constructor() {
        // create canvas
        this.canvas = document.getElementById("renderCanvas");
        // set config
        this.config = new Config();
        // initialize babylon scene and engine
        this._init();
    }
    _init() {
        return __awaiter(this, void 0, void 0, function* () {
            // create engine — URL flags allow alternative WebGL paths to be tried on
            // devices where the default one fails to draw (see Utils/engineOptions).
            const engineOptions = engineOptionsFromQuery(window.location.search);
            console.info("[ENGINE] options:", describeEngineOptions(engineOptions));
            this.engine = new Engine(this.canvas, engineOptions.antialias, {
                adaptToDeviceRatio: engineOptions.adaptToDeviceRatio,
                antialias: engineOptions.antialias,
                disableWebGL2Support: engineOptions.disableWebGL2Support,
            });
            // Must be set before any SpriteManager is built: the sprite renderer reads
            // these capabilities once, in its constructor.
            if (engineOptions.disableVertexArrayObjects) {
                this.engine.disableVertexArrayObjects = true;
            }
            if (engineOptions.disableInstancing) {
                this.engine.getCaps().instancedArrays = false;
            }
            console.info("[ENGINE] WebGL " + this.engine.webGLVersion +
                " · max texture " + this.engine.getCaps().maxTextureSize +
                " · " + (this.engine.getGlInfo ? this.engine.getGlInfo().renderer : "renderer unknown"));
            //
            this.engine.setHardwareScalingLevel(1);
            // loading
            var loadingScreen = new Loading("Loading Assets...");
            this.engine.loadingScreen = loadingScreen;
            // preload game data
            this.game = new GameController(this);
            yield this.game.initializeGameData();
            // set default scene
            let defaultScene = isLocal() ? State.GAME : State.LOGIN;
            this.game.setScene(defaultScene);
            // main render loop & state machine
            yield this._render();
        });
    }
    _render() {
        return __awaiter(this, void 0, void 0, function* () {
            // render loop
            this.engine.runRenderLoop(() => {
                // monitor state
                this.game.state = this.checkForSceneChange();
                switch (this.game.state) {
                    ///////////////////////////////////////
                    // LOGIN SCENE
                    case State.LOGIN:
                        this.clearScene();
                        this.game.currentScene = new LoginScene();
                        this.createScene();
                        break;
                    ///////////////////////////////////////
                    // CHARACTER SELECTION SCENE
                    case State.CHARACTER_SELECTION:
                        this.clearScene();
                        this.game.currentScene = new CharacterSelectionScene();
                        this.createScene();
                        break;
                    ///////////////////////////////////////
                    // CHARACTER SELECTION SCENE
                    case State.CHARACTER_EDITOR:
                        this.clearScene();
                        this.game.currentScene = new CharacterEditor();
                        this.createScene();
                        break;
                    ///////////////////////////////////////
                    // GAME
                    case State.GAME:
                        this.clearScene();
                        this.game.currentScene = new GameScene();
                        this.createScene();
                        break;
                    ///////////////////////////////////////
                    // DEBUG
                    case State.DEBUG_SCENE:
                        this.clearScene();
                        this.game.currentScene = new DebugScene();
                        this.createScene();
                        break;
                    default:
                        break;
                }
                // render when scene is ready
                this._process();
            });
            //for development: make inspector visible/invisible
            if (isLocal()) {
                window.addEventListener("keydown", (ev) => {
                    //Shift+Ctrl+Alt+I
                    if (ev.shiftKey && ev.ctrlKey && ev.altKey && ev.keyCode === 73) {
                        if (this.game.scene.debugLayer.isVisible()) {
                            this.game.scene.debugLayer.hide();
                        }
                        else {
                            this.game.scene.debugLayer.show();
                        }
                    }
                });
            }
            //resize if the screen is resized/rotated
            window.addEventListener("resize", () => {
                this.engine.resize();
                if (this.game.currentScene && this.game.currentScene.resize) {
                    this.game.currentScene.resize();
                }
            });
        });
    }
    createScene() {
        this.game.currentScene.createScene(this.game);
        this.game.scene = this.game.currentScene._scene;
        this.game.state = State.NULL;
    }
    checkForSceneChange() {
        let currentScene = this.game.nextScene;
        if (this.game.nextScene !== State.NULL) {
            this.game.nextScene = State.NULL;
            return currentScene;
        }
    }
    _process() {
        return __awaiter(this, void 0, void 0, function* () {
            // make sure scene and camera is initialized
            if (this.game.scene && this.game.scene.activeCamera) {
                // render scene
                //this.game.currentScene.engineUpdate();
                this.game.scene.render();
            }
        });
    }
    clearScene() {
        if (this.game.scene) {
            this.game.engine.displayLoadingUI();
            this.game.scene.detachControl();
            this.game.scene.dispose();
            this.game.currentScene = null;
        }
    }
}
new App();
